import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Add,
  Search,
  CalendarToday,
  ViewList,
  MoreVert,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Schedule,
  Person,
  LocationOn,
  Phone,
  Email,
  Directions,
  Close,
  EventAvailable,
  EventBusy,
  AccessTime,
  PersonOff,
} from '@mui/icons-material';
import { format, addDays, startOfMonth, endOfMonth, isSameDay, parseISO } from 'date-fns';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../services/api';

const APPOINTMENT_TYPES = [
  'Initial Consultation',
  'Property Walk-through',
  'Contract Signing',
  'Follow-up Meeting',
  'Inventory Assessment',
  'Sale Day Briefing',
];

const APPOINTMENT_STATUSES = {
  scheduled: { label: 'Scheduled', color: 'info', icon: Schedule },
  confirmed: { label: 'Confirmed', color: 'success', icon: CheckCircle },
  completed: { label: 'Completed', color: 'default', icon: EventAvailable },
  cancelled: { label: 'Cancelled', color: 'error', icon: Cancel },
  'no-show': { label: 'No Show', color: 'warning', icon: PersonOff },
};

const DURATIONS = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
];

const Appointments = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [sales, setSales] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    duration: 60,
    appointment_type: 'Initial Consultation',
    client_type: 'lead', // 'lead' or 'client'
    client_id: null,
    lead_id: null,
    property_address: '',
    staff_id: null,
    notes: '',
    send_reminder: true,
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchAppointments(),
        fetchLeads(),
        fetchClients(),
        fetchSales(),
        fetchStaff(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await api.get('/appointments');
      const appointmentsData = response.data?.appointments || response.data || [];
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      // Use mock data if API doesn't exist
      setAppointments(generateMockAppointments());
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads/');
      const leadsData = response.data?.leads || response.data || [];
      setLeads(leadsData);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/auth/users');
      const users = response.data?.users || response.data || [];
      const clientUsers = users.filter(user => user.role === 'client');
      setClients(clientUsers);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const fetchSales = async () => {
    try {
      const response = await api.get('/sales/');
      const salesData = response.data?.sales || response.data || [];
      setSales(salesData);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api.get('/auth/users');
      const users = response.data?.users || response.data || [];
      const staffUsers = users.filter(user => ['admin', 'staff'].includes(user.role));
      setStaff(staffUsers);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaff([]);
    }
  };

  const generateMockAppointments = () => {
    const now = new Date();
    return [
      {
        id: 1,
        date: format(addDays(now, 1), 'yyyy-MM-dd'),
        time: '10:00',
        duration: 60,
        appointment_type: 'Initial Consultation',
        client_name: 'John Smith',
        client_type: 'lead',
        property_address: '123 Main St, Boston, MA',
        staff_name: 'Sarah Johnson',
        status: 'confirmed',
        notes: 'First-time client, interested in estate liquidation',
        created_at: now.toISOString(),
      },
      {
        id: 2,
        date: format(addDays(now, 2), 'yyyy-MM-dd'),
        time: '14:00',
        duration: 120,
        appointment_type: 'Property Walk-through',
        client_name: 'Mary Wilson',
        client_type: 'client',
        property_address: '456 Oak Ave, Cambridge, MA',
        staff_name: 'Michael Brown',
        status: 'scheduled',
        notes: 'Large estate, needs full inventory assessment',
        created_at: now.toISOString(),
      },
      {
        id: 3,
        date: format(addDays(now, -1), 'yyyy-MM-dd'),
        time: '09:00',
        duration: 60,
        appointment_type: 'Contract Signing',
        client_name: 'Robert Davis',
        client_type: 'client',
        property_address: '789 Elm St, Brookline, MA',
        staff_name: 'Sarah Johnson',
        status: 'completed',
        notes: 'Contract signed, deposit received',
        created_at: now.toISOString(),
      },
    ];
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleMenuOpen = (event, appointment) => {
    setAnchorEl(event.currentTarget);
    setSelectedAppointment(appointment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenCreateDialog = (dateStr = null) => {
    const now = new Date();
    let appointmentDate = now;
    let appointmentTime = '09:00';

    if (dateStr) {
      appointmentDate = new Date(dateStr);
      appointmentTime = '09:00';
    }

    setFormData({
      date: format(appointmentDate, 'yyyy-MM-dd'),
      time: appointmentTime,
      duration: 60,
      appointment_type: 'Initial Consultation',
      client_type: 'lead',
      client_id: null,
      lead_id: null,
      property_address: '',
      staff_id: staff.length > 0 ? staff[0].id : null,
      notes: '',
      send_reminder: true,
    });
    setSelectedAppointment(null);
    setFormErrors({});
    setCreateDialogOpen(true);
  };

  const handleEditAppointment = () => {
    if (!selectedAppointment) return;

    setFormData({
      date: selectedAppointment.date,
      time: selectedAppointment.time,
      duration: selectedAppointment.duration,
      appointment_type: selectedAppointment.appointment_type,
      client_type: selectedAppointment.client_type || 'lead',
      client_id: selectedAppointment.client_id || null,
      lead_id: selectedAppointment.lead_id || null,
      property_address: selectedAppointment.property_address || '',
      staff_id: selectedAppointment.staff_id || null,
      notes: selectedAppointment.notes || '',
      send_reminder: false,
    });
    setFormErrors({});
    setDetailDialogOpen(false);
    setCreateDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      await api.delete(`/appointments/${selectedAppointment.id}`);
      showSnackbar('Appointment deleted successfully', 'success');
      handleCloseDeleteDialog();
      fetchAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      showSnackbar('Failed to delete appointment', 'error');
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setDetailDialogOpen(true);
    handleMenuClose();
  };

  const handleStatusChange = async (status) => {
    if (!selectedAppointment) return;

    try {
      await api.patch(`/appointments/${selectedAppointment.id}`, { status });
      showSnackbar(`Appointment ${status}`, 'success');
      setDetailDialogOpen(false);
      handleMenuClose();
      fetchAppointments();
    } catch (error) {
      console.error('Error updating appointment status:', error);
      showSnackbar('Failed to update appointment status', 'error');
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.date) errors.date = 'Date is required';
    if (!formData.time) errors.time = 'Time is required';
    if (!formData.appointment_type) errors.appointment_type = 'Type is required';
    if (formData.client_type === 'lead' && !formData.lead_id) {
      errors.lead_id = 'Please select a lead';
    }
    if (formData.client_type === 'client' && !formData.client_id) {
      errors.client_id = 'Please select a client';
    }
    if (!formData.staff_id) errors.staff_id = 'Please assign staff';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        datetime: `${formData.date}T${formData.time}:00`,
      };

      if (selectedAppointment) {
        await api.put(`/appointments/${selectedAppointment.id}`, submitData);
        showSnackbar('Appointment updated successfully', 'success');
      } else {
        await api.post('/appointments', submitData);
        showSnackbar('Appointment created successfully', 'success');
      }

      setCreateDialogOpen(false);
      fetchAppointments();
    } catch (error) {
      console.error('Error saving appointment:', error);
      showSnackbar('Failed to save appointment', 'error');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  const handleCheckAvailability = () => {
    if (!formData.date || !formData.staff_id) {
      showSnackbar('Please select date and staff first', 'warning');
      return;
    }

    // Generate mock available slots
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      slots.push({
        time: `${hour.toString().padStart(2, '0')}:00`,
        available: Math.random() > 0.3,
      });
      if (hour < 17) {
        slots.push({
          time: `${hour.toString().padStart(2, '0')}:30`,
          available: Math.random() > 0.3,
        });
      }
    }

    setAvailableSlots(slots);
    setAvailabilityDialogOpen(true);
  };

  const handleSelectTimeSlot = (time) => {
    handleFormChange('time', time);
    setAvailabilityDialogOpen(false);
  };

  const handleDateClick = (arg) => {
    handleOpenCreateDialog(arg.dateStr);
  };

  const handleEventClick = (clickInfo) => {
    const appointment = appointments.find(
      a => a.id.toString() === clickInfo.event.id
    );
    if (appointment) {
      handleViewDetails(appointment);
    }
  };

  const getFilteredAppointments = () => {
    return appointments.filter((appointment) => {
      const search = searchQuery.toLowerCase();
      const matchesSearch =
        appointment.client_name?.toLowerCase().includes(search) ||
        appointment.property_address?.toLowerCase().includes(search) ||
        appointment.appointment_type?.toLowerCase().includes(search) ||
        appointment.staff_name?.toLowerCase().includes(search);

      const matchesStatus =
        statusFilter === 'all' || appointment.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  const formatEventsForCalendar = () => {
    return appointments.map((appointment) => {
      const statusConfig = APPOINTMENT_STATUSES[appointment.status] || APPOINTMENT_STATUSES.scheduled;
      const colors = {
        info: '#2196f3',
        success: '#4caf50',
        default: '#9e9e9e',
        error: '#f44336',
        warning: '#ff9800',
      };

      return {
        id: appointment.id.toString(),
        title: `${appointment.appointment_type} - ${appointment.client_name}`,
        start: `${appointment.date}T${appointment.time}`,
        end: `${appointment.date}T${addMinutesToTime(appointment.time, appointment.duration)}`,
        backgroundColor: colors[statusConfig.color],
        borderColor: colors[statusConfig.color],
        extendedProps: appointment,
      };
    });
  };

  const addMinutesToTime = (time, minutes) => {
    const [hours, mins] = time.split(':').map(Number);
    const totalMins = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMins / 60) % 24;
    const newMins = totalMins % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  };

  const getContactDetails = () => {
    if (!selectedAppointment) return null;

    if (selectedAppointment.client_type === 'lead') {
      const lead = leads.find(l => l.id === selectedAppointment.lead_id);
      return lead;
    } else {
      const client = clients.find(c => c.id === selectedAppointment.client_id);
      return client;
    }
  };

  const getPropertyOptions = () => {
    return sales.map(sale => ({
      label: `${sale.address}, ${sale.city}, ${sale.state}`,
      value: `${sale.address}, ${sale.city}, ${sale.state}`,
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const filteredAppointments = getFilteredAppointments();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Appointments
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Schedule and manage client meetings
          </Typography>
        </div>
        <Box display="flex" gap={1}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="list">
              <ViewList fontSize="small" sx={{ mr: 0.5 }} />
              List
            </ToggleButton>
            <ToggleButton value="calendar">
              <CalendarToday fontSize="small" sx={{ mr: 0.5 }} />
              Calendar
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenCreateDialog()}
          >
            New Appointment
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search appointments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {Object.entries(APPOINTMENT_STATUSES).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      {config.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary" textAlign="right">
                {filteredAppointments.length} appointment{filteredAppointments.length !== 1 ? 's' : ''}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Client/Lead</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Staff</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAppointments.length > 0 ? (
                    filteredAppointments.map((appointment) => {
                      const statusConfig = APPOINTMENT_STATUSES[appointment.status] || APPOINTMENT_STATUSES.scheduled;
                      const StatusIcon = statusConfig.icon;

                      return (
                        <TableRow key={appointment.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {format(parseISO(appointment.date), 'MMM dd, yyyy')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                <AccessTime fontSize="inherit" />
                                {appointment.time} ({appointment.duration} min)
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={appointment.appointment_type}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                {appointment.client_name?.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {appointment.client_name}
                                </Typography>
                                <Chip
                                  label={appointment.client_type === 'lead' ? 'Lead' : 'Client'}
                                  size="small"
                                  sx={{ height: 16, fontSize: '0.65rem' }}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                              <LocationOn fontSize="small" color="action" />
                              {appointment.property_address || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {appointment.staff_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<StatusIcon />}
                              label={statusConfig.label}
                              size="small"
                              color={statusConfig.color}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="More actions">
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, appointment)}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box py={6}>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchQuery || statusFilter !== 'all'
                              ? 'No appointments found'
                              : 'No appointments yet'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" mb={3}>
                            {searchQuery || statusFilter !== 'all'
                              ? 'Try adjusting your search or filters'
                              : 'Create your first appointment to get started'}
                          </Typography>
                          {!searchQuery && statusFilter === 'all' && (
                            <Button
                              variant="contained"
                              startIcon={<Add />}
                              onClick={() => handleOpenCreateDialog()}
                            >
                              New Appointment
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card>
          <CardContent>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              events={formatEventsForCalendar()}
              editable={false}
              selectable={true}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              height="auto"
              slotMinTime="08:00:00"
              slotMaxTime="19:00:00"
              allDaySlot={false}
              eventTimeFormat={{
                hour: 'numeric',
                minute: '2-digit',
                meridiem: 'short',
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedAppointment && handleViewDetails(selectedAppointment)}>
          <ListItemIcon>
            <CalendarToday fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditAppointment}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {selectedAppointment?.status === 'scheduled' && (
          <MenuItem onClick={() => handleStatusChange('confirmed')}>
            <ListItemIcon>
              <CheckCircle fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Confirm</ListItemText>
          </MenuItem>
        )}
        {['scheduled', 'confirmed'].includes(selectedAppointment?.status) && (
          <MenuItem onClick={() => handleStatusChange('completed')}>
            <ListItemIcon>
              <EventAvailable fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark Complete</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleOpenDeleteDialog}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Appointment Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {selectedAppointment ? 'Edit Appointment' : 'New Appointment'}
            <IconButton onClick={() => setCreateDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => handleFormChange('date', e.target.value)}
                error={!!formErrors.date}
                helperText={formErrors.date}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  label="Time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleFormChange('time', e.target.value)}
                  error={!!formErrors.time}
                  helperText={formErrors.time}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <Tooltip title="Check availability">
                  <IconButton onClick={handleCheckAvailability} color="primary">
                    <Schedule />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Duration</InputLabel>
                <Select
                  value={formData.duration}
                  label="Duration"
                  onChange={(e) => handleFormChange('duration', e.target.value)}
                >
                  {DURATIONS.map((duration) => (
                    <MenuItem key={duration.value} value={duration.value}>
                      {duration.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.appointment_type}>
                <InputLabel>Appointment Type</InputLabel>
                <Select
                  value={formData.appointment_type}
                  label="Appointment Type"
                  onChange={(e) => handleFormChange('appointment_type', e.target.value)}
                >
                  {APPOINTMENT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Client Type</InputLabel>
                <Select
                  value={formData.client_type}
                  label="Client Type"
                  onChange={(e) => handleFormChange('client_type', e.target.value)}
                >
                  <MenuItem value="lead">Lead</MenuItem>
                  <MenuItem value="client">Client</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.client_type === 'lead' ? (
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!formErrors.lead_id}>
                  <InputLabel>Select Lead</InputLabel>
                  <Select
                    value={formData.lead_id || ''}
                    label="Select Lead"
                    onChange={(e) => handleFormChange('lead_id', e.target.value)}
                  >
                    {leads.map((lead) => (
                      <MenuItem key={lead.id} value={lead.id}>
                        {lead.first_name} {lead.last_name} - {lead.email}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.lead_id && (
                    <Typography variant="caption" color="error">
                      {formErrors.lead_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!formErrors.client_id}>
                  <InputLabel>Select Client</InputLabel>
                  <Select
                    value={formData.client_id || ''}
                    label="Select Client"
                    onChange={(e) => handleFormChange('client_id', e.target.value)}
                  >
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.first_name} {client.last_name} - {client.email}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.client_id && (
                    <Typography variant="caption" color="error">
                      {formErrors.client_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={getPropertyOptions()}
                value={formData.property_address}
                onChange={(e, newValue) => {
                  handleFormChange('property_address', typeof newValue === 'string' ? newValue : newValue?.value || '');
                }}
                onInputChange={(e, newValue) => {
                  handleFormChange('property_address', newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Property Address"
                    placeholder="Enter or select property address"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required error={!!formErrors.staff_id}>
                <InputLabel>Assign Staff</InputLabel>
                <Select
                  value={formData.staff_id || ''}
                  label="Assign Staff"
                  onChange={(e) => handleFormChange('staff_id', e.target.value)}
                >
                  {staff.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name} - {member.role}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.staff_id && (
                  <Typography variant="caption" color="error">
                    {formErrors.staff_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                multiline
                rows={3}
                placeholder="Add appointment notes..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.send_reminder}
                    onChange={(e) => handleFormChange('send_reminder', e.target.checked)}
                  />
                }
                label="Send email reminder 24 hours before appointment"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedAppointment ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Appointment Details
            <IconButton onClick={() => setDetailDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAppointment && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Appointment Type
                </Typography>
                <Chip label={selectedAppointment.appointment_type} />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  icon={React.createElement(APPOINTMENT_STATUSES[selectedAppointment.status]?.icon || Schedule)}
                  label={APPOINTMENT_STATUSES[selectedAppointment.status]?.label || 'Scheduled'}
                  color={APPOINTMENT_STATUSES[selectedAppointment.status]?.color || 'default'}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Date & Time
                </Typography>
                <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                  <AccessTime fontSize="small" />
                  {format(parseISO(selectedAppointment.date), 'MMMM dd, yyyy')} at {selectedAppointment.time}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {selectedAppointment.duration} minutes
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Client Information
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Person fontSize="small" />
                  <Typography variant="body1">
                    {selectedAppointment.client_name}
                  </Typography>
                  <Chip
                    label={selectedAppointment.client_type === 'lead' ? 'Lead' : 'Client'}
                    size="small"
                  />
                </Box>
                {(() => {
                  const contact = getContactDetails();
                  return contact ? (
                    <Box display="flex" flexDirection="column" gap={1} ml={4}>
                      {contact.email && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="body2">{contact.email}</Typography>
                        </Box>
                      )}
                      {contact.phone && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Phone fontSize="small" color="action" />
                          <Typography variant="body2">{contact.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  ) : null;
                })()}
              </Box>

              {selectedAppointment.property_address && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Property Address
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body1">
                      {selectedAppointment.property_address}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Directions />}
                    onClick={() => {
                      const address = encodeURIComponent(selectedAppointment.property_address);
                      window.open(`https://maps.google.com/?q=${address}`, '_blank');
                    }}
                    sx={{ mt: 1 }}
                  >
                    Get Directions
                  </Button>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Assigned Staff
                </Typography>
                <Typography variant="body1">
                  {selectedAppointment.staff_name || 'Not assigned'}
                </Typography>
              </Box>

              {selectedAppointment.notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedAppointment.notes}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {['scheduled', 'confirmed'].includes(selectedAppointment.status) && (
                <Box display="flex" gap={1} mt={2}>
                  {selectedAppointment.status === 'scheduled' && (
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleStatusChange('confirmed')}
                      fullWidth
                    >
                      Confirm
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EventAvailable />}
                    onClick={() => handleStatusChange('completed')}
                    fullWidth
                  >
                    Complete
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleStatusChange('cancelled')}
                    fullWidth
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          <Button
            onClick={handleEditAppointment}
            variant="contained"
            startIcon={<Edit />}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Appointment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently delete the appointment.
          </Alert>
          <Typography>
            Are you sure you want to delete this appointment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteAppointment} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Checker Dialog */}
      <Dialog
        open={availabilityDialogOpen}
        onClose={() => setAvailabilityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Available Time Slots
          <Typography variant="caption" display="block" color="text.secondary">
            {formData.date && format(parseISO(formData.date), 'MMMM dd, yyyy')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1}>
            {availableSlots.map((slot) => (
              <Grid item xs={6} sm={4} key={slot.time}>
                <Button
                  fullWidth
                  variant={slot.available ? 'outlined' : 'text'}
                  disabled={!slot.available}
                  onClick={() => handleSelectTimeSlot(slot.time)}
                  sx={{
                    justifyContent: 'center',
                    color: slot.available ? 'primary.main' : 'text.disabled',
                  }}
                >
                  {slot.time}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailabilityDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Appointments;
